import { THEMES, useThemeCntx } from "@/context/ThemeContext";
import { cn } from "@/utils/helpers";
import { Camera, Geometry, Mesh, Program, Renderer, Transform } from "ogl";
import { useEffect, useRef } from "react";
const vertexShader = `
attribute vec3 position;
attribute vec3 normal;
attribute vec2 uv;

uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat3 normalMatrix;

varying vec3 vNormal;
varying vec3 vPosition;
varying vec2 vUv;

void main() {
  vUv = uv;
  vNormal = normalize(normalMatrix * normal);
  vPosition = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;
const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;

varying vec3 vNormal;
varying vec3 vPosition;
varying vec2 vUv;

const float IOR = 1.5;
const float POWER_EXPONENT = 6.0;
const float MASK_MULTIPLIER_1 = 10000.0;
const float MASK_MULTIPLIER_2 = 9500.0;
const float MASK_MULTIPLIER_3 = 11000.0;
const float LENS_MULTIPLIER = 5000.0;
const float MASK_STRENGTH_1 = 8.0;
const float MASK_STRENGTH_2 = 16.0;
const float MASK_STRENGTH_3 = 2.0;
const float MASK_THRESHOLD_1 = 0.95;
const float MASK_THRESHOLD_2 = 0.9;
const float MASK_THRESHOLD_3 = 1.5;
const float GRADIENT_RANGE = 0.2;
const float GRADIENT_OFFSET = 0.1;
const float LIGHTING_INTENSITY = 0.3;

float fresnel(vec3 viewDirection, vec3 normal, float ior) {
  float cosTheta = dot(viewDirection, normal);
  float r0 = pow((1.0 - ior) / (1.0 + ior), 2.0);
  return r0 + (1.0 - r0) * pow(1.0 - abs(cosTheta), 5.0);
}

float roundedBox(vec2 pos, float aspect) {
  return pow(abs(pos.x * aspect), POWER_EXPONENT) + pow(abs(pos.y), POWER_EXPONENT);
}

vec3 createMasks(vec2 pos, float aspect) {
  float roundedBox = roundedBox(pos, aspect);
  
  float rb1 = clamp((1.0 - roundedBox * MASK_MULTIPLIER_1) * MASK_STRENGTH_1, 0.0, 1.0);
  float rb2 = clamp((MASK_THRESHOLD_1 - roundedBox * MASK_MULTIPLIER_2) * MASK_STRENGTH_2, 0.0, 1.0) -
    clamp(pow(MASK_THRESHOLD_2 - roundedBox * MASK_MULTIPLIER_2, 1.0) * MASK_STRENGTH_2, 0.0, 1.0);
  float rb3 = clamp((MASK_THRESHOLD_3 - roundedBox * MASK_MULTIPLIER_3) * MASK_STRENGTH_3, 0.0, 1.0) -
    clamp(pow(1.0 - roundedBox * MASK_MULTIPLIER_3, 1.0) * MASK_STRENGTH_3, 0.0, 1.0);
  
  return vec3(rb1, rb2, rb3);
}

vec2 lensDistortion(vec2 uv, vec2 pos, float aspect) {
  float roundedBox = roundedBox(pos, aspect);
  float distortion = (1.0 - roundedBox * LENS_MULTIPLIER);
  return (uv - 0.5) * distortion + 0.5;
}

float gradientLighting(vec2 pos, float rb3) {
  float gradient = clamp((clamp(pos.y, 0.0, GRADIENT_RANGE) + GRADIENT_OFFSET) / 2.0, 0.0, 1.0) +
    clamp((clamp(-pos.y, -1000.0, GRADIENT_RANGE) * rb3 + GRADIENT_OFFSET) / 2.0, 0.0, 1.0);
  return gradient;
}

void main() {
  vec3 viewDirection = normalize(-vPosition);
  vec3 normal = normalize(vNormal);
  
  vec2 pos2D = vec2(vPosition.x, vPosition.y);
  float aspect = 1.0; // Can be adjusted based on view
  
  vec3 masks = createMasks(pos2D, aspect);
  float rb1 = masks.x;
  float rb2 = masks.y;
  float rb3 = masks.z;
  
  float fresnelFactor = fresnel(viewDirection, normal, IOR);
  
  float edgeDistance = length(vPosition);
  float rimLight = smoothstep(0.7, 0.92, edgeDistance);
  
  vec3 glassColor = vec3(0.95, 0.97, 1.0);
  
  float transition = smoothstep(0.0, 1.0, rb1 + rb2);
  
  float gradient = gradientLighting(pos2D, rb3);
  
  vec3 lighting = glassColor + vec3(rb1) * gradient * 2.0 + vec3(rb2) * LIGHTING_INTENSITY * 2.5;
  glassColor = mix(glassColor, lighting, transition);
  
  vec3 rimColor = vec3(1.0, 1.0, 1.0);
  float sharpRim = smoothstep(0.8, 0.98, edgeDistance);
  glassColor += rimColor * sharpRim * 8.0;
  
  float edgeHighlight = smoothstep(0.15, 0.95, fresnelFactor);
  glassColor += vec3(1.0) * edgeHighlight * 4.5;
  
  float internalGlow = smoothstep(0.5, 0.75, edgeDistance) * (1.0 - smoothstep(0.85, 1.0, edgeDistance));
  glassColor += vec3(0.9, 0.95, 1.0) * internalGlow * 2.0;
  
  float edgeDefinition = smoothstep(0.88, 1.0, edgeDistance);
  glassColor += vec3(1.0) * edgeDefinition * 5.0;
  
  vec3 lightDir = normalize(vec3(1.0, 1.0, 1.0));
  vec3 reflectDir = reflect(-lightDir, normal);
  float specular = pow(max(dot(reflectDir, viewDirection), 0.0), 128.0);
  glassColor += vec3(1.0) * specular * 3.0;
  
  vec3 lightDir2 = normalize(vec3(-0.5, 1.0, 1.0));
  vec3 reflectDir2 = reflect(-lightDir2, normal);
  float specular2 = pow(max(dot(reflectDir2, viewDirection), 0.0), 256.0);

  glassColor += vec3(1.0) * specular2 * 4.0;
  glassColor *= vec3(1.0, 0.98, 0.96);
  glassColor *= 1.2;
  
  float centerAlpha = 0.3; // More visible center
  float edgeAlpha = 0.98; // Very defined edges
  float alphaMix = fresnelFactor * 0.65 + sharpRim * 0.35;
  float alpha = mix(centerAlpha, edgeAlpha, alphaMix);
  
  alpha += rb1 * 0.3;
  alpha += rb2 * 0.25;
  alpha += specular2 * 0.25;
  alpha += edgeHighlight * 0.2;
  alpha += edgeDefinition * 0.15;
  
  // Ensure good visibility with defined edges
  alpha = clamp(alpha, 0.25, 0.98);
  
  gl_FragColor = vec4(glassColor, alpha);
}
`;
const createTorusGeometry = (gl: WebGLRenderingContext & {
  renderer: Renderer;
  canvas: HTMLCanvasElement;
}, radius: number, tube: number, radialSegments: number, tubularSegments: number) => {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= radialSegments; i++) {
    for (let j = 0; j <= tubularSegments; j++) {
      const u = j / tubularSegments * Math.PI * 2;
      const v = i / radialSegments * Math.PI * 2;
      const x = (radius + tube * Math.cos(v)) * Math.cos(u);
      const y = (radius + tube * Math.cos(v)) * Math.sin(u);
      const z = tube * Math.sin(v);
      positions.push(x, y, z);
      const nx = Math.cos(u) * Math.cos(v);
      const ny = Math.sin(u) * Math.cos(v);
      const nz = Math.sin(v);
      normals.push(nx, ny, nz);
      uvs.push(j / tubularSegments, i / radialSegments);
    }
  }
  for (let i = 0; i < radialSegments; i++) {
    for (let j = 0; j < tubularSegments; j++) {
      const a = i * (tubularSegments + 1) + j;
      const b = a + tubularSegments + 1;
      indices.push(a, b, a + 1);
      indices.push(b, b + 1, a + 1);
    }
  }
  return new Geometry(gl, {
    position: {
      size: 3,
      data: new Float32Array(positions)
    },
    normal: {
      size: 3,
      data: new Float32Array(normals)
    },
    uv: {
      size: 2,
      data: new Float32Array(uvs)
    },
    index: {
      data: new Uint16Array(indices)
    }
  });
};
const getThemeColors = (theme: string): [number, number, number] => {
  return theme === THEMES.dark ? [0.3, 0.4, 0.8] // Dark theme: blue-purple
  : [0.6, 0.7, 0.9]; // Light theme: light blue
};
interface TorusGlassProps {
  size?: number;
  className?: string;
}
export const TorusGlass = ({
  size = 1,
  className = ""
}: TorusGlassProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    theme
  } = useThemeCntx();
  const animationIdRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const programRef = useRef<Program | null>(null);
  useEffect(() => {
    if (!programRef.current) return;
    programRef.current.uniforms.uColor.value = getThemeColors(theme);
  }, [theme]);
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    const existingCanvas = container.querySelector("canvas");
    if (existingCanvas) {
      container.removeChild(existingCanvas);
      const oldGl = existingCanvas.getContext("webgl");
      if (oldGl) {
        oldGl.getExtension("WEBGL_lose_context")?.loseContext();
      }
    }
    const renderer = new Renderer({
      alpha: true,
      antialias: true,
      powerPreference: "high-performance",
      premultipliedAlpha: false
    });
    const gl = renderer.gl as WebGLRenderingContext & {
      renderer: Renderer;
      canvas: HTMLCanvasElement;
    };
    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.enable(gl.CULL_FACE);
    gl.canvas.style.backgroundColor = "transparent";
    gl.canvas.style.opacity = "0";
    gl.canvas.style.visibility = "hidden";
    const camera = new Camera(gl, {
      fov: 75
    });
    camera.position.set(0, 0, 5);
    const scene = new Transform();
    const geometry = createTorusGeometry(gl, 0.7 * size, 0.4 * size, 100, 60);
    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: {
          value: 0
        },
        uColor: {
          value: getThemeColors(theme)
        }
      },
      transparent: true,
      cullFace: null
    });
    programRef.current = program;
    const mesh = new Mesh(gl, {
      geometry,
      program
    });
    mesh.setParent(scene);
    let animationId: number;
    function resize() {
      const width = container.offsetWidth;
      const height = container.offsetHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      renderer.setSize(width * dpr, height * dpr);
      gl.canvas.style.width = `${width}px`;
      gl.canvas.style.height = `${height}px`;
      camera.perspective({
        aspect: width / height
      });
    }
    window.addEventListener("resize", resize);
    resize();
    function update(t: number) {
      animationId = requestAnimationFrame(update);
      mesh.rotation.x = t * 0.0005;
      mesh.rotation.y = t * 0.0001;
      program.uniforms.uTime.value = t * 0.001;
      renderer.render({
        scene,
        camera
      });
    }
    gl.canvas.style.position = "absolute";
    gl.canvas.style.top = "0";
    gl.canvas.style.left = "0";
    gl.canvas.style.width = "100%";
    gl.canvas.style.height = "100%";
    gl.canvas.style.pointerEvents = "none";
    gl.canvas.style.backgroundColor = "transparent";
    container.appendChild(gl.canvas);
    function showNewCanvas() {
      if (!container.contains(gl.canvas)) return;
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      renderer.render({
        scene,
        camera
      });
      gl.canvas.style.visibility = "visible";
      gl.canvas.style.transition = "opacity 0.15s ease-in";
      gl.canvas.style.opacity = "1";
    }
    requestAnimationFrame(() => {
      showNewCanvas();
    });
    animationId = requestAnimationFrame(update);
    animationIdRef.current = animationId;
    cleanupRef.current = () => {
      window.removeEventListener("resize", resize);
      if (animationIdRef.current !== null) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      const allCanvases = container.querySelectorAll("canvas");
      allCanvases.forEach(canvas => {
        if (canvas !== gl.canvas && container.contains(canvas)) {
          container.removeChild(canvas);
          const oldGl = canvas.getContext("webgl");
          if (oldGl) {
            oldGl.getExtension("WEBGL_lose_context")?.loseContext();
          }
        }
      });
      if (container.contains(gl.canvas)) {
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      }
    };
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      programRef.current = null;
    };
    // this is a performance optimization to avoid re-calculating the element once the theme changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);
  return <div ref={containerRef} className={cn("relative w-full h-full aspect-square", className)} />;
};